import {
  ElementRef,
  inject,
  NgModule,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ReactiveFormsModule } from '@angular/forms';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { ExcelModule, GridModule } from '@progress/kendo-angular-grid';
import { DialogsModule } from '@progress/kendo-angular-dialog';
import { InputsModule } from '@progress/kendo-angular-inputs';
import { IconsModule } from '@progress/kendo-angular-icons';
import { MenusModule } from '@progress/kendo-angular-menu';
import { NavigationModule } from '@progress/kendo-angular-navigation';
import { NOTIFICATION_CONTAINER } from '@progress/kendo-angular-notification';
import { LayoutModule, PanelBarModule } from '@progress/kendo-angular-layout';
import { ScrollViewModule } from '@progress/kendo-angular-scrollview';
import { ListViewModule } from '@progress/kendo-angular-listview';
import { LabelModule } from '@progress/kendo-angular-label';
import {
  DateInputModule,
  DateInputsModule,
} from '@progress/kendo-angular-dateinputs';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { PopupModule } from '@progress/kendo-angular-popup';
import { ExcelExportModule } from '@progress/kendo-angular-excel-export';
import { FormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { OConfigService } from '@olameter/o-config-service';
import {
  OColorHelper,
  OFormHelper,
  ONotificationService,
} from '@olameter/o-utilities';
import { environment } from '../environments/environment';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage,
} from 'ngx-webstorage';
import { WorkOrderActionsGRPCService } from './ProxyServices/workorderactions.grpc.service';
import { WorkorderGRPCService } from './ProxyServices/workorder.grpc.service';
import { ViewConfigGRPCService } from './ProxyServices/view-config.grpc.service';
import { UserGRPCService } from './ProxyServices/user.grpc.service';
import { NgxMapLibreGLModule } from '@maplibre/ngx-maplibre-gl';
import { DispatchRecallComponent } from './home/components/dispatch-recall/dispatch-recall.component';
import { RecallDialogComponent } from './home/components/recalldialog/recall-dialog.component';
import { HoldPopUpComponent } from './home/components/holdpopup/holdpopup.component';
import { LegendControlComponent } from './home/components/legend-control/legend-control.component';
import { MasterFilterComponent } from './home/components/master-filter/master-filter.component';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { ConfigWorkflowGRPCService } from './ProxyServices/config-workflow.grpc.service';
import { GenericEnumValueGRPCService } from './ProxyServices/genericenumvalue.grpc.service';
import { SchedulerGRPCService } from './ProxyServices/scheduler.grpc.service';
import { ContractGRPCService } from './ProxyServices/contract.grpc.service';
import { CustomFiltersModule } from './CommonFiles/utilities/components/filters/filters.module';
import { InstalldDateGRPCService } from './ProxyServices/installdate.grpc.service';
import { DataSelectorPopupComponent } from './home/components/data-selector-popup/data-selector-popup.component';
import { BlackoutComponent } from './home/components/blackout/blackout.component';
import { CommunicationCenterGRPCService } from './ProxyServices/communicationcenter.grpc.service';
import { ActionsRibbonComponent } from './home/components/actions-ribbon/actions-ribbon.component';
import { PipesModule } from './CommonFiles/utilities/pipes/pipes.module';
import { MassStatusUpdateComponent } from './home/components/mass-status-update/mass-status-update.component';

import { InstallDatePopupComponent } from './home/components/installdatepopup/install-date-popup.component';
import { HomeComponent } from './home/home.component';
import { SchedulerModule } from '@progress/kendo-angular-scheduler';
import { DropDownsModule } from '@progress/kendo-angular-dropdowns';
import { WorkOrdersMapComponent } from './home/components/workorders-map/workorders-map.component';
import { IndicatorsModule } from '@progress/kendo-angular-indicators';

import { AGGridComponent } from './home/components/ag-grid/ag-grid.component';

import { AgGridAngular } from 'ag-grid-angular'; // Angular Data Grid Component
import { SelectedRowsFilter } from './home/components/selected-filter/selected-filter';
import { DetailPageRenderer } from './home/components/row-details/row-details';
import { BlackoutCountdownDaysCellRenderer } from './home/components/row-blackout-countdown-days/row-blackout-countdown-days';
import { KendoGridComponent } from './home/components/kendo-grid/kendo-grid.component';
import { ViewsComponent } from './home/components/views/views.component';
import { ViewItemComponent } from './home/components/view-item/view-item.component';
import { ViewSaveComponent } from './home/components/view-save/view-save.component';
import { LocationStrategy, PathLocationStrategy } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptorsFromDi,
  withJsonpSupport,
} from '@angular/common/http';
import {
  provideTranslateService,
  TranslateModule,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import {
  OAuthService,
  OAuthenticationModule,
} from '@olameter/o-authentication';
import { OMenuComponent } from '@olameter/o-menu';
import { WONotesGRPCService } from './ProxyServices/wo-notes.grpc.service';
import { SharedModule } from './shared/shared.module';

import { ModuleRegistry } from 'ag-grid-community';
import { AllEnterpriseModule } from 'ag-grid-enterprise';

ModuleRegistry.registerModules([AllEnterpriseModule]);

export function initConfig(appConfig: OConfigService) {
  return () => {
    return appConfig.loadConfigAsyn(
      environment.configLocation,
      environment.zkparentNode,
      environment.aclDigests
    );
  };
}

@NgModule({
  declarations: [
    AppComponent,
    HomeComponent,
    DispatchRecallComponent,
    RecallDialogComponent,
    InstallDatePopupComponent,
    HoldPopUpComponent,
    LegendControlComponent,
    MasterFilterComponent,
    ActionsRibbonComponent,
    DataSelectorPopupComponent,
    BlackoutComponent,
    WorkOrdersMapComponent,
    AGGridComponent,
    SelectedRowsFilter,
    DetailPageRenderer,
    BlackoutCountdownDaysCellRenderer,
    KendoGridComponent,
    ViewsComponent,
    ViewItemComponent,
    ViewSaveComponent,
    MassStatusUpdateComponent
  ],
  imports: [
    AppRoutingModule,
    BrowserModule,
    BrowserAnimationsModule,
    ReactiveFormsModule,
    NgxMapLibreGLModule,
    GridModule,
    InputsModule,
    DateInputModule,
    DateInputsModule,
    ButtonsModule,
    LabelModule,
    FormsModule,
    IconsModule,
    MenusModule,
    NavigationModule,
    FontAwesomeModule,
    LayoutModule,
    PanelBarModule,
    PopupModule,
    TreeViewModule,
    DialogsModule,
    ScrollViewModule,
    ListViewModule,
    CustomFiltersModule,
    ExcelExportModule,
    ExcelModule,
    SchedulerModule,
    DropDownsModule,
    IndicatorsModule,
    TooltipModule,
    PipesModule,
    AgGridAngular,
    OAuthenticationModule,
    OMenuComponent,
    SharedModule,
    TranslateModule
  ],
  providers: [
    provideAppInitializer(() => {
      const initializerFn = initConfig(inject(OConfigService));
      return initializerFn();
    }),
    ONotificationService,
    UserGRPCService,
    WorkorderGRPCService,
    ViewConfigGRPCService,
    WorkOrderActionsGRPCService,
    ConfigWorkflowGRPCService,
    SchedulerGRPCService,
    ContractGRPCService,
    GenericEnumValueGRPCService,
    InstalldDateGRPCService,
    CommunicationCenterGRPCService,
    WONotesGRPCService,
    OFormHelper,
    OColorHelper,
    {
      provide: NOTIFICATION_CONTAINER,
      useFactory: () => {
        //return the container ElementRef, where the notification will be injected
        return { nativeElement: document.body } as ElementRef;
      },
    },
    provideNgxWebstorage(
      withNgxWebstorageConfig({ separator: ':', caseSensitive: true }),
      withLocalStorage(),
      withSessionStorage()
    ),
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy,
    },
    provideHttpClient(withInterceptorsFromDi(), withJsonpSupport(), withFetch()),
    OAuthService,


    provideZoneChangeDetection({ eventCoalescing: true }),
    provideTranslateService({
      loader: provideTranslateHttpLoader({
        prefix: '/assets/i18n/',
        suffix: '.json'
      }),
      fallbackLang: 'en',
      lang: 'en'
    })
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
